import { useState, useEffect, useRef } from "react";
import { supabase } from "../lib/supabase";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { LogOut, User as UserIcon, FileSignature, Camera, Loader2, UserCircle, ChevronDown } from "lucide-react";
import { User } from "@supabase/supabase-js";
import { MyAgreementsModal } from "./MyAgreementsModal";
import { getProfile, updateProfile, Profile } from "../lib/profiles";
import { uploadAvatar } from "../lib/avatarUpload";
import { getProfileSchema, ProfileFormData } from "../lib/validations/profileSchema";
import { toast } from "sonner";
import { MobilePicker, MobilePickerOption } from "./ui/mobile-picker";

export function UserAvatar() {
  const { t, i18n } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isAgreementsOpen, setIsAgreementsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [bioLength, setBioLength] = useState(0);
  const [isRolePickerOpen, setIsRolePickerOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string>('renter');

  const userRole = profile?.role || 'user';
  const profileSchema = getProfileSchema(userRole as 'user' | 'admin' | 'broker');
  
  // Role options with custom colors
  const roleOptions: MobilePickerOption[] = [
    { 
      value: 'broker', 
      label: 'Broker',
      color: 'text-purple-700',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200'
    },
    { 
      value: 'seller', 
      label: 'Seller',
      color: 'text-blue-700',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200'
    },
    { 
      value: 'renter', 
      label: 'Renter',
      color: 'text-green-700',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200'
    },
    { 
      value: 'buyer', 
      label: 'Buyer',
      color: 'text-orange-700',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200'
    },
  ];
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: '',
      whatsapp_number: '',
      bio: '',
      website_url: '',
      agency_name: '',
      license_number: '',
    },
  });

  const bioValue = watch('bio');
  
  useEffect(() => {
    setBioLength(bioValue?.length || 0);
  }, [bioValue]);

  const currentLang = i18n.language?.startsWith('he') ? 'he' : 'en';

  const handleLanguageChange = (lang: string) => {
    if (lang) {
      i18n.changeLanguage(lang);
    }
  };

  useEffect(() => {
    const loadUserAndProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      
      if (user) {
        const profileData = await getProfile(user.id);
        if (profileData) {
          setProfile(profileData);
          // Set avatar preview
          if (profileData.avatar_url) {
            setAvatarPreview(profileData.avatar_url);
          }
        }
      }
    };
    
    loadUserAndProfile();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setUser(session?.user ?? null);
      
      if (session?.user) {
        const profileData = await getProfile(session.user.id);
        if (profileData) {
          setProfile(profileData);
          if (profileData.avatar_url) {
            setAvatarPreview(profileData.avatar_url);
          }
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Update form when profile dialog opens
  useEffect(() => {
    if (isProfileOpen && profile) {
      console.log('[UserAvatar] Dialog opened with profile:', {
        profile,
        userRole,
        isProfileOpen,
        hasAvatar: !!avatarPreview
      });
      
      reset({
        name: profile.name || '',
        whatsapp_number: profile.whatsapp_number || '',
        bio: profile.bio || '',
        website_url: profile.website_url || '',
        agency_name: profile.agency_name || '',
        license_number: profile.license_number || '',
      });
      setAvatarPreview(profile.avatar_url);
    }
    
    if (!isProfileOpen) {
      console.log('[UserAvatar] Dialog closed');
    }
  }, [isProfileOpen, profile, reset]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    setIsUploadingAvatar(true);
    try {
      // Show preview immediately
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      // Upload to Supabase Storage
      const { url, error } = await uploadAvatar(file, user.id, profile?.avatar_url || null);
      
      if (error) {
        toast.error(error);
        // Revert preview
        setAvatarPreview(profile?.avatar_url || null);
        return;
      }

      if (url) {
        // Update profile in database
        const success = await updateProfile({ avatar_url: url });
        if (success) {
          // Update local profile state
          if (profile) {
            setProfile({ ...profile, avatar_url: url });
          }
          toast.success(t('profile.avatarUploaded') || 'Avatar updated!');
        } else {
          toast.error(t('profile.errors.uploadFailed') || 'Failed to update avatar');
          setAvatarPreview(profile?.avatar_url || null);
        }
      }
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error(t('profile.errors.uploadFailed') || 'Failed to upload avatar');
      setAvatarPreview(profile?.avatar_url || null);
    } finally {
      setIsUploadingAvatar(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const onSubmit = async (data: ProfileFormData) => {
    if (!user) return;

    console.log('[UserAvatar] Form submit started:', {
      data,
      userId: user.id,
      userRole
    });

    setIsSaving(true);
    try {
      // Update profile in database
      const success = await updateProfile({
        name: data.name,
        whatsapp_number: data.whatsapp_number || null,
        bio: data.bio || null,
        website_url: data.website_url || null,
        agency_name: data.agency_name || null,
        license_number: data.license_number || null,
      });

      if (success) {
        console.log('[UserAvatar] Profile update successful');
        
        // Update local profile state
        if (profile) {
          setProfile({
            ...profile,
            name: data.name,
            whatsapp_number: data.whatsapp_number || null,
            bio: data.bio || null,
            website_url: data.website_url || null,
            agency_name: data.agency_name || null,
            license_number: data.license_number || null,
          });
        }
        
        toast.success(t('profile.saved') || 'Profile updated!');
        setIsProfileOpen(false);
      } else {
        console.error('[UserAvatar] Profile update failed - returned false');
        toast.error(t('profile.saveFailed') || 'Failed to save profile. Please try again.');
      }
    } catch (error) {
      console.error('[UserAvatar] Profile update error:', error);
      toast.error(t('profile.saveFailed') || 'Failed to save profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!user) return null;

  const initials = profile?.name?.substring(0, 2).toUpperCase() || user?.email?.substring(0, 2).toUpperCase() || 'U';

  return (
    <>
      <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <div 
            role="button"
            tabIndex={0}
            className="rounded-full focus:outline-none ring-2 ring-white/20 hover:ring-white/40 transition-all overflow-hidden cursor-pointer"
            style={{
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              flexShrink: 0,
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsDropdownOpen(true);
              }
            }}
          >
            <Avatar 
              className="h-9 w-9 border-2 border-white/20"
              style={{
                borderRadius: '50%',
                overflow: 'hidden',
                width: '100%',
                height: '100%',
              }}
            >
              <AvatarImage 
                src={profile?.avatar_url || undefined} 
                alt={profile?.name || user?.email || 'User'}
                style={{
                  borderRadius: '50%',
                }}
              />
              <AvatarFallback className="bg-white/10 text-white text-xs font-medium">
                {initials}
              </AvatarFallback>
            </Avatar>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64 bg-white/95 backdrop-blur-md border-gray-200 shadow-xl rounded-xl p-2">
          <div className="px-3 py-3 border-b border-gray-100">
            <p className="text-sm font-semibold text-gray-900 truncate">
              {profile?.name || t('profile.anonymous')}
            </p>
            <p className="text-xs text-gray-500 truncate mt-0.5">
              {user?.email}
            </p>
            <div className={`mt-2 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold capitalize border-2 ${
              roleOptions.find(r => r.value === selectedRole)?.bgColor || 'bg-gray-100'
            } ${
              roleOptions.find(r => r.value === selectedRole)?.color || 'text-gray-700'
            } ${
              roleOptions.find(r => r.value === selectedRole)?.borderColor || 'border-gray-200'
            }`}>
              {roleOptions.find(r => r.value === selectedRole)?.label || selectedRole}
            </div>
          </div>
          
          <div className="py-1">
            <DropdownMenuItem 
              onClick={() => setIsProfileOpen(true)}
              className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
            >
              <UserIcon className="h-4 w-4 text-gray-500" />
              <span>{t('profile.editProfile')}</span>
            </DropdownMenuItem>
            
            <DropdownMenuItem 
              onClick={() => setIsAgreementsOpen(true)}
              className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
            >
              <FileSignature className="h-4 w-4 text-gray-500" />
              <span>{t('profile.myAgreements')}</span>
            </DropdownMenuItem>
          </div>

          <DropdownMenuSeparator />
          
          <div className="px-3 py-3">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 px-1">
              {t('profile.language')}
            </p>
            <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
              <button
                onClick={() => handleLanguageChange('en')}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  currentLang === 'en'
                    ? 'bg-purple-600 text-white shadow-md'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                EN
              </button>
              <button
                onClick={() => handleLanguageChange('he')}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  currentLang === 'he'
                    ? 'bg-purple-600 text-white shadow-md'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                עברית
              </button>
            </div>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={handleSignOut}
            className="text-red-600 focus:text-red-600 focus:bg-red-50 rounded-lg cursor-pointer"
          >
            <LogOut className="h-4 w-4 mr-2" />
            <span>{t('profile.signOut')}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={isProfileOpen} onOpenChange={setIsProfileOpen}>
        <DialogContent 
          variant="medium"
          className="!flex !flex-col !p-0 !gap-0 !max-w-[calc(100vw-32px)] sm:!max-w-[550px] !max-h-[85vh] !overflow-hidden"
        >
          <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <DialogTitle>{t('profile.title')}</DialogTitle>
            <DialogDescription>
              {t('profile.description')}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 overflow-hidden">
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {/* Avatar Section */}
              <div className="flex flex-col items-center gap-3 pt-2">
                <div className="relative">
                  <Avatar className="h-24 w-24 border-4 border-gray-100 shadow-sm">
                    <AvatarImage 
                      src={avatarPreview || undefined} 
                      alt={profile?.name || 'User'} 
                    />
                    <AvatarFallback className="bg-purple-100 text-purple-600 text-2xl font-semibold">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  
                  {/* Camera Icon Button */}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploadingAvatar}
                    className="absolute bottom-0 right-0 p-2 bg-purple-600 text-white rounded-full shadow-lg hover:bg-purple-700 transition-all hover:scale-110 active:scale-95 disabled:bg-gray-400"
                    aria-label={t('profile.uploadAvatar')}
                  >
                    {isUploadingAvatar ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Camera className="h-4 w-4" />
                    )}
                  </button>
                  
                  {/* Hidden File Input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleAvatarChange}
                    className="hidden"
                    style={{ display: 'none' }}
                    aria-hidden="true"
                  />
                </div>

                {/* User Details */}
                <div className="text-center space-y-1">
                  <h2 className="text-xl font-bold text-gray-900">{profile?.name || t('profile.anonymous')}</h2>
                  <p className="text-sm text-gray-500 font-medium">{user?.email}</p>
                  <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700 capitalize border border-purple-200">
                    {userRole}
                  </div>
                </div>
              </div>

              {/* Role Selector */}
              <div className="space-y-2">
                <Label htmlFor="role" className="text-sm font-medium">
                  I am a <span className="text-red-500">*</span>
                </Label>
                <button
                  type="button"
                  onClick={() => setIsRolePickerOpen(true)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border-2 transition-all ${
                    roleOptions.find(r => r.value === selectedRole)?.bgColor || 'bg-gray-50'
                  } ${
                    roleOptions.find(r => r.value === selectedRole)?.borderColor || 'border-gray-200'
                  } hover:shadow-sm active:scale-[0.99]`}
                >
                  <span className={`text-sm font-semibold ${
                    roleOptions.find(r => r.value === selectedRole)?.color || 'text-gray-700'
                  }`}>
                    {roleOptions.find(r => r.value === selectedRole)?.label || 'Select Role'}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>
              </div>

              {/* Display Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium">
                  {t('profile.name')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  type="text"
                  placeholder={t('profile.namePlaceholder')}
                  {...register('name')}
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && (
                  <p className="text-xs text-red-500">{errors.name.message}</p>
                )}
              </div>

              {/* WhatsApp Number */}
              <div className="space-y-2">
                <Label htmlFor="whatsapp_number" className="text-sm font-medium">
                  {t('profile.whatsapp')}
                </Label>
                <Input
                  id="whatsapp_number"
                  type="tel"
                  placeholder="+972-50-123-4567"
                  {...register('whatsapp_number')}
                  className={errors.whatsapp_number ? 'border-red-500' : ''}
                />
                {errors.whatsapp_number ? (
                  <p className="text-xs text-red-500">{errors.whatsapp_number.message}</p>
                ) : (
                  <p className="text-xs text-gray-500">{t('profile.whatsappHelp')}</p>
                )}
              </div>

              {/* Bio */}
              <div className="space-y-2">
                <Label htmlFor="bio" className="text-sm font-medium">
                  {t('profile.bio')}
                </Label>
                <Textarea
                  id="bio"
                  placeholder={t('profile.bioPlaceholder')}
                  {...register('bio', {
                    onChange: (e) => {
                      setBioLength(e.target.value?.length || 0);
                    }
                  })}
                  className={`${errors.bio ? 'border-red-500' : ''} min-h-[80px] resize-none`}
                  rows={3}
                  maxLength={160}
                  onKeyDown={(e) => {
                    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Tab'];
                    if (bioLength >= 160 && !allowedKeys.includes(e.key) && !e.ctrlKey && !e.metaKey) {
                      e.preventDefault();
                    }
                  }}
                />
                <div className="flex justify-between items-center">
                  {errors.bio ? (
                    <p className="text-xs text-red-500">{errors.bio.message}</p>
                  ) : (
                    <span className="text-xs text-gray-500">{t('profile.bioHint') || 'Tell people a bit about yourself'}</span>
                  )}
                  <span className={`text-xs font-medium transition-colors ${
                    bioLength >= 160 ? 'text-red-500' : 
                    bioLength >= 150 ? 'text-orange-500' : 
                    'text-gray-500'
                  }`}>
                    {bioLength}/160
                  </span>
                </div>
              </div>

              {/* Website / Social Link */}
              <div className="space-y-2">
                <Label htmlFor="website_url" className="text-sm font-medium">
                  {t('profile.website')}
                </Label>
                <Input
                  id="website_url"
                  type="url"
                  placeholder={t('profile.websitePlaceholder') || 'https://...'}
                  {...register('website_url')}
                  className={errors.website_url ? 'border-red-500' : ''}
                />
                {errors.website_url && (
                  <p className="text-xs text-red-500">{errors.website_url.message}</p>
                )}
              </div>

              {/* Broker-Specific Fields */}
              {(userRole === 'broker' || userRole === 'admin') && (
                <div className="pt-3 space-y-3">
                  {/* Agency Name */}
                  <div className="space-y-2">
                    <Label htmlFor="agency_name" className="text-sm font-medium">
                      {t('profile.agencyName')}
                    </Label>
                    <Input
                      id="agency_name"
                      type="text"
                      placeholder={t('profile.agencyNamePlaceholder') || 'Your agency name'}
                      {...register('agency_name')}
                      className={errors.agency_name ? 'border-red-500' : ''}
                    />
                    {errors.agency_name && (
                      <p className="text-xs text-red-500">{errors.agency_name.message}</p>
                    )}
                  </div>

                  {/* License Number */}
                  <div className="space-y-2">
                    <Label htmlFor="license_number" className="text-sm font-medium">
                      {t('profile.licenseNumber')}
                    </Label>
                    <Input
                      id="license_number"
                      type="text"
                      placeholder={t('profile.licenseNumberPlaceholder') || 'License #'}
                      {...register('license_number')}
                      className={errors.license_number ? 'border-red-500' : ''}
                    />
                    {errors.license_number && (
                      <p className="text-xs text-red-500">{errors.license_number.message}</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons - Sticky Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t bg-white shrink-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsProfileOpen(false)}
                disabled={isSaving}
              >
                {t('profile.cancel')}
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('profile.saving')}
                  </>
                ) : (
                  t('profile.save')
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <MyAgreementsModal
        isOpen={isAgreementsOpen}
        onClose={() => setIsAgreementsOpen(false)}
      />

      {/* Role Picker Drawer */}
      <MobilePicker
        open={isRolePickerOpen}
        onOpenChange={setIsRolePickerOpen}
        title="I am a"
        icon={UserCircle}
        options={roleOptions}
        value={selectedRole}
        onChange={setSelectedRole}
        renderOption={(option, isSelected) => (
          <button
            type="button"
            className={`w-full flex items-center justify-between px-4 py-4 transition-all ${
              isSelected 
                ? `${option.bgColor} border-l-4 ${option.borderColor}` 
                : 'hover:bg-gray-50 active:bg-gray-100'
            }`}
          >
            <span className={`text-sm font-semibold ${
              isSelected ? option.color : 'text-gray-700'
            }`}>
              {option.label}
            </span>
            {isSelected && (
              <div className={`w-2 h-2 rounded-full ${option.bgColor} ${option.borderColor} border-2`} />
            )}
          </button>
        )}
      />
    </>
  );
}

